import React from 'react';
import { Button } from 'react-bootstrap';
import { Plus, Trash2, Layers, Zap, Copy, PlusCircle } from 'lucide-react';
import { SPECIFICATION_PRESETS, formatINR } from '../../../../helper/bom';

const ASSEMBLY_TYPES = [
  "Electrical Panel",
  "Structural Fabrication",
  "Mechanical Assembly",
  "Piping & Skid",
  "Custom Product"
];

const AssemblyTreeBuilder = ({
  assemblies,
  setAssemblies,
  itemMasterList = []
}) => {

  const addBlankAssembly = () => {
    const newIdx = assemblies.length + 1;
    setAssemblies([
      ...assemblies,
      {
        assembly_code: String(newIdx),
        assembly_name: "",
        assembly_type: "Electrical Panel",
        quantity: 1,
        unit: "NOS",
        unit_rate: "",
        total_price: 0,
        material_cost: 0,
        labor_cost: 0,
        overhead_cost: 0,
        margin_percent: 0,
        sub_assemblies: [
          {
            code: "A",
            name: "",
            quantity: 1,
            description: "",
            items: [
              {
                sr_no: 1,
                item_name: "",
                description: "",
                make: "",
                size_dimension: "",
                hsn_code: "",
                unit: "NOS",
                unit_quantity: 1,
                total_quantity: 1,
                rate: "",
                amount: 0,
                is_free_issue: false,
                remarks: ""
              }
            ]
          }
        ],
        specifications: []
      }
    ]);
  };

  const loadSampleAssembly = () => {
    const sample = {
      assembly_code: "1",
      assembly_name: "APFC PANEL (60KVAR)",
      assembly_type: "Electrical Panel",
      quantity: 1,
      unit: "NOS",
      unit_rate: 60000,
      total_price: 60000,
      material_cost: 0,
      labor_cost: 0,
      overhead_cost: 0,
      margin_percent: 0,
      sub_assemblies: [
        {
          code: "A",
          name: "APFC INCOMER",
          quantity: 1,
          items: [
            { sr_no: 1, item_name: "125A 3P 36kA MCCB TM Release", description: "Incomer Switchgear", make: "LK", unit: "NOS", unit_quantity: 1, total_quantity: 1, rate: 12500, amount: 12500 },
            { sr_no: 2, item_name: "Red/Yellow/Blue LED Indicators 22.5mm", description: "Phase Indication", make: "ESBEE", unit: "SET", unit_quantity: 1, total_quantity: 1, rate: 450, amount: 450 },
            { sr_no: 3, item_name: "600/5A Current Transformer Class 1.0", description: "Metering CT", make: "TRINITY", unit: "NOS", unit_quantity: 1, total_quantity: 1, rate: 850, amount: 850 },
            { sr_no: 4, item_name: "6 Steps APFC Relay Controller", description: "Microprocessor Based", make: "LK", unit: "NOS", unit_quantity: 1, total_quantity: 1, rate: 6500, amount: 6500 }
          ]
        },
        {
          code: "B",
          name: "5KVAR CAPACITOR STEP",
          quantity: 1,
          items: [
            { sr_no: 1, item_name: "10A 3P 10kA C-Curve MCB", description: "Step Protection", make: "LK", unit: "NOS", unit_quantity: 1, total_quantity: 1, rate: 650, amount: 650 },
            { sr_no: 2, item_name: "5kVAr 3Ph Capacitor Duty Contactor", description: "440V Coil", make: "LK", unit: "NOS", unit_quantity: 1, total_quantity: 1, rate: 1800, amount: 1800 },
            { sr_no: 3, item_name: "5kVAr 3Ph Gas Filled 440V Capacitor", description: "MPP Heavy Duty", make: "SUBODHAN", unit: "NOS", unit_quantity: 1, total_quantity: 1, rate: 2400, amount: 2400 }
          ]
        }
      ],
      specifications: SPECIFICATION_PRESETS.ELECTRICAL_PANEL
    };

    setAssemblies([...assemblies, sample]);
  };

  const removeAssembly = (aIdx) => {
    setAssemblies(assemblies.filter((_, i) => i !== aIdx));
  };

  const cloneAssembly = (aIdx) => {
    const cloned = JSON.parse(JSON.stringify(assemblies[aIdx]));
    cloned.assembly_code = String(assemblies.length + 1);
    cloned.assembly_name = (cloned.assembly_name || "Panel") + " (Copy)";
    setAssemblies([...assemblies, cloned]);
  };

  const updateAssemblyField = (aIdx, field, val) => {
    const updated = [...assemblies];
    updated[aIdx][field] = val;
    if (field === "quantity" || field === "unit_rate") {
      const q = parseFloat(updated[aIdx].quantity) || 1;
      const r = parseFloat(updated[aIdx].unit_rate) || 0;
      updated[aIdx].total_price = Number((q * r).toFixed(2));
    }
    setAssemblies(updated);
  };

  const addSubAssembly = (aIdx) => {
    const updated = [...assemblies];
    const subList = updated[aIdx].sub_assemblies || [];
    const nextCode = String.fromCharCode(65 + subList.length);
    subList.push({
      code: nextCode,
      name: "",
      quantity: 1,
      description: "",
      items: [
        {
          sr_no: 1,
          item_name: "",
          description: "",
          make: "",
          size_dimension: "",
          hsn_code: "",
          unit: "NOS",
          unit_quantity: 1,
          total_quantity: 1,
          rate: "",
          amount: 0,
          is_free_issue: false,
          remarks: ""
        }
      ]
    });
    updated[aIdx].sub_assemblies = subList;
    setAssemblies(updated);
  };

  const removeSubAssembly = (aIdx, sIdx) => {
    const updated = [...assemblies];
    updated[aIdx].sub_assemblies = updated[aIdx].sub_assemblies.filter((_, i) => i !== sIdx);
    setAssemblies(updated);
  };

  const updateSubField = (aIdx, sIdx, field, val) => {
    const updated = [...assemblies];
    updated[aIdx].sub_assemblies[sIdx][field] = val;

    const boardQty = parseFloat(updated[aIdx].quantity) || 1;
    const feederQty = parseFloat(val) || 1;

    (updated[aIdx].sub_assemblies[sIdx].items || []).forEach(item => {
      const uQty = parseFloat(item.unit_quantity) || 0;
      const r = parseFloat(item.rate) || 0;
      item.total_quantity = Number((boardQty * feederQty * uQty).toFixed(2));
      item.amount = Number((item.total_quantity * r).toFixed(2));
    });

    setAssemblies(updated);
  };

  const addBoqItem = (aIdx, sIdx) => {
    const updated = [...assemblies];
    const items = updated[aIdx].sub_assemblies[sIdx].items || [];
    items.push({
      sr_no: items.length + 1,
      item_name: "",
      description: "",
      make: "",
      size_dimension: "",
      hsn_code: "",
      unit: "NOS",
      unit_quantity: 1,
      total_quantity: 1,
      rate: "",
      amount: 0,
      is_free_issue: false,
      remarks: ""
    });
    updated[aIdx].sub_assemblies[sIdx].items = items;
    setAssemblies(updated);
  };

  const removeBoqItem = (aIdx, sIdx, iIdx) => {
    const updated = [...assemblies];
    updated[aIdx].sub_assemblies[sIdx].items = updated[aIdx].sub_assemblies[sIdx].items.filter((_, i) => i !== iIdx);
    setAssemblies(updated);
  };

  const updateBoqItemField = (aIdx, sIdx, iIdx, field, val) => {
    const updated = [...assemblies];
    const item = updated[aIdx].sub_assemblies[sIdx].items[iIdx];
    item[field] = val;

    if (field === "item_name") {
      const found = itemMasterList.find(it => it.name?.toLowerCase() === val.toLowerCase());
      if (found) {
        item.item_id = found._id;
        item.hsn_code = found.hsn_code || "";
        item.unit = found.unit?.name || item.unit || "NOS";
        item.rate = found.sale_rate || found.purchase_rate || item.rate || "";
        item.description = found.detail || item.description || "";
      }
    }

    const boardQty = parseFloat(updated[aIdx].quantity) || 1;
    const feederQty = parseFloat(updated[aIdx].sub_assemblies[sIdx].quantity) || 1;
    const unitQty = parseFloat(item.unit_quantity) || 0;
    const rate = parseFloat(item.rate) || 0;

    item.total_quantity = Number((boardQty * feederQty * unitQty).toFixed(2));
    item.amount = Number((item.total_quantity * rate).toFixed(2));

    setAssemblies(updated);
  };

  return (
    <div className="bom-assemblies-builder">
      {/* Master Item Datalist for Fast Auto-complete */}
      <datalist id="bomMasterItemsList">
        {itemMasterList.map((it) => (
          <option key={it._id} value={it.name}>
            {it.detail ? `${it.detail} (${it.category?.name || ''})` : ''}
          </option>
        ))}
      </datalist>

      {/* Top Action Toolbar */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h6 className="mb-0 fw-bold d-flex align-items-center gap-2" style={{ color: "#1e293b", fontSize: "14px" }}>
            <Layers size={16} className="text-primary" />
            Major Assemblies & BOQ Line Items ({assemblies.length})
          </h6>
          <small className="text-muted" style={{ fontSize: "12px" }}>
            Define panels, equipment units, sub-assemblies/feeders, and material BOQ items
          </small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-warning"
            size="sm"
            onClick={loadSampleAssembly}
            className="py-1 px-3 rounded text-dark fw-semibold"
            style={{ fontSize: "12px" }}
            title="Load sample APFC panel"
          >
            <Zap size={14} className="me-1" /> ⚡ Load Sample APFC Panel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={addBlankAssembly}
            className="py-1 px-3 rounded fw-semibold"
            style={{ fontSize: "12px" }}
          >
            <Plus size={14} className="me-1" /> + Add Blank Assembly
          </Button>
        </div>
      </div>

      {assemblies.length === 0 ? (
        <div className="text-center py-5 border rounded bg-white text-muted shadow-sm">
          <Layers size={36} className="text-muted mb-2" />
          <h6 className="fw-bold mb-1" style={{ fontSize: "14px" }}>No Assemblies Added Yet</h6>
          <p className="small text-muted mb-3" style={{ fontSize: "12px" }}>
            Click below to add a blank assembly or load a sample electrical panel template
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="primary" size="sm" onClick={addBlankAssembly} className="py-1 px-3 fw-semibold" style={{ fontSize: "12px" }}>
              <Plus size={13} className="me-1" /> Add Assembly Row
            </Button>
            <Button variant="outline-warning" size="sm" onClick={loadSampleAssembly} className="py-1 px-3 text-dark fw-semibold" style={{ fontSize: "12px" }}>
              <Zap size={13} className="me-1" /> Load Sample APFC Panel
            </Button>
          </div>
        </div>
      ) : (
        assemblies.map((assembly, aIdx) => (
          <div key={aIdx} className="border rounded-3 mb-4 bg-white shadow-sm overflow-hidden" style={{ borderColor: "#cbd5e1" }}>
            {/* Clean Light Assembly Header (No Heavy Dark Colors) */}
            <div
              className="p-2 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2 border-bottom"
              style={{ backgroundColor: "#f8fafc" }}
            >
              <div className="d-flex align-items-center gap-2 flex-grow-1 flex-wrap">
                <span className="badge bg-primary px-2 py-1 fw-bold" style={{ fontSize: "12px" }}>
                  #{aIdx + 1}
                </span>
                <input
                  type="text"
                  className="form-control fw-bold"
                  style={{ minWidth: "360px", flex: "1 1 360px", height: "36px", fontSize: "13px", borderColor: "#cbd5e1" }}
                  value={assembly.assembly_name}
                  placeholder="Enter Panel / Assembly Name (e.g. APFC PANEL 60KVAR)"
                  onChange={(e) => updateAssemblyField(aIdx, "assembly_name", e.target.value)}
                />
                <select
                  className="form-select"
                  style={{ width: "190px", height: "36px", fontSize: "12.5px", borderColor: "#cbd5e1" }}
                  value={assembly.assembly_type}
                  onChange={(e) => updateAssemblyField(aIdx, "assembly_type", e.target.value)}
                >
                  {ASSEMBLY_TYPES.map((t, idx) => (
                    <option key={idx} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-1">
                  <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>Qty:</span>
                  <input
                    type="number"
                    className="form-control text-center fw-bold"
                    style={{ width: "65px", height: "36px", fontSize: "12.5px", borderColor: "#cbd5e1" }}
                    min="1"
                    value={assembly.quantity}
                    onChange={(e) => updateAssemblyField(aIdx, "quantity", e.target.value)}
                  />
                </div>

                <div className="d-flex align-items-center gap-1">
                  <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>Rate (₹):</span>
                  <input
                    type="number"
                    className="form-control text-end fw-bold"
                    style={{ width: "120px", height: "36px", fontSize: "12.5px", borderColor: "#cbd5e1" }}
                    placeholder="Rate"
                    value={assembly.unit_rate}
                    onChange={(e) => updateAssemblyField(aIdx, "unit_rate", e.target.value)}
                  />
                </div>

                <div className="text-end px-2" style={{ minWidth: "120px" }}>
                  <span className="text-muted d-block" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>TOTAL PRICE</span>
                  <span className="fw-bold text-primary" style={{ fontSize: "15px" }}>
                    {formatINR(assembly.total_price)}
                  </span>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm p-1 px-2"
                  title="Clone Assembly"
                  onClick={() => cloneAssembly(aIdx)}
                >
                  <Copy size={15} />
                </button>

                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm p-1 px-2 text-danger"
                  title="Delete Assembly"
                  onClick={() => removeAssembly(aIdx)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>

            {/* Feeder & BOQ Items Container */}
            <div className="p-3 bg-light">
              {(assembly.sub_assemblies || []).map((sub, sIdx) => (
                <div key={sIdx} className="border rounded bg-white mb-3 shadow-sm overflow-hidden" style={{ borderColor: "#e2e8f0" }}>
                  {/* Clean Feeder Header with Wide Input */}
                  <div className="d-flex justify-content-between align-items-center py-2 px-3 bg-white border-bottom flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <span className="badge bg-dark px-2 py-1 fw-bold" style={{ fontSize: "11px" }}>{sub.code}</span>
                      <input
                        type="text"
                        className="form-control fw-bold"
                        style={{ minWidth: "350px", flex: "1 1 350px", height: "34px", fontSize: "12.5px", borderColor: "#cbd5e1" }}
                        value={sub.name}
                        placeholder="Feeder Name (e.g. APFC INCOMER / 5KVAR STEP / CONTROLLING)"
                        onChange={(e) => updateSubField(aIdx, sIdx, "name", e.target.value)}
                      />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted fw-semibold" style={{ fontSize: "12px" }}>Multiplier / Feeder Qty:</span>
                      <input
                        type="number"
                        className="form-control text-center fw-bold"
                        style={{ width: "60px", height: "34px", fontSize: "12px", borderColor: "#cbd5e1" }}
                        min="1"
                        value={sub.quantity}
                        onChange={(e) => updateSubField(aIdx, sIdx, "quantity", e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm p-1"
                        disabled={assembly.sub_assemblies.length === 1}
                        onClick={() => removeSubAssembly(aIdx, sIdx)}
                        title="Delete Feeder"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Clean High-Density BOQ Table */}
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0 align-middle" style={{ fontSize: "12px" }}>
                      <thead style={{ backgroundColor: "#f8fafc" }}>
                        <tr>
                          <th style={{ width: "3%", padding: "6px 8px" }} className="text-center">#</th>
                          <th style={{ width: "32%", padding: "6px 8px" }}>Item Name / Master Description</th>
                          <th style={{ width: "20%", padding: "6px 8px" }}>Specification / Detail</th>
                          <th style={{ width: "13%", padding: "6px 8px" }}>Make / Brand</th>
                          <th style={{ width: "6%", padding: "6px 8px" }} className="text-center">Unit</th>
                          <th style={{ width: "6%", padding: "6px 8px" }} className="text-center">Qty</th>
                          <th style={{ width: "6%", padding: "6px 8px" }} className="text-center">Tot Qty</th>
                          <th style={{ width: "8%", padding: "6px 8px" }} className="text-end">Rate (₹)</th>
                          <th style={{ width: "9%", padding: "6px 8px" }} className="text-end">Amount (₹)</th>
                          <th style={{ width: "3%", padding: "6px 8px" }} className="text-center"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sub.items || []).map((item, iIdx) => (
                          <tr key={iIdx}>
                            <td className="text-center text-muted fw-bold py-1" style={{ fontSize: "11px" }}>{iIdx + 1}</td>
                            <td className="py-1 px-2">
                              <input
                                list="bomMasterItemsList"
                                type="text"
                                className="form-control form-control-sm py-1"
                                style={{ height: "30px", fontSize: "12px" }}
                                placeholder="Type item name or search master..."
                                value={item.item_name}
                                onChange={(e) => updateBoqItemField(aIdx, sIdx, iIdx, "item_name", e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-2">
                              <input
                                type="text"
                                className="form-control form-control-sm py-1"
                                style={{ height: "30px", fontSize: "12px" }}
                                placeholder="Size / Rating / Specs"
                                value={item.description}
                                onChange={(e) => updateBoqItemField(aIdx, sIdx, iIdx, "description", e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-2">
                              <input
                                type="text"
                                className="form-control form-control-sm py-1"
                                style={{ height: "30px", fontSize: "12px" }}
                                placeholder="Brand / Make"
                                value={item.make}
                                onChange={(e) => updateBoqItemField(aIdx, sIdx, iIdx, "make", e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-1">
                              <input
                                type="text"
                                className="form-control form-control-sm py-1 text-center"
                                style={{ height: "30px", fontSize: "11.5px" }}
                                value={item.unit}
                                onChange={(e) => updateBoqItemField(aIdx, sIdx, iIdx, "unit", e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-1">
                              <input
                                type="number"
                                className="form-control form-control-sm py-1 text-center"
                                style={{ height: "30px", fontSize: "12px" }}
                                min="0.01"
                                step="0.01"
                                value={item.unit_quantity}
                                onChange={(e) => updateBoqItemField(aIdx, sIdx, iIdx, "unit_quantity", e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-1 text-center fw-bold bg-light" style={{ fontSize: "12px" }}>
                              {item.total_quantity}
                            </td>
                            <td className="py-1 px-2">
                              <input
                                type="number"
                                className="form-control form-control-sm py-1 text-end"
                                style={{ height: "30px", fontSize: "12px" }}
                                min="0"
                                step="0.01"
                                placeholder="0"
                                value={item.rate}
                                onChange={(e) => updateBoqItemField(aIdx, sIdx, iIdx, "rate", e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-2 text-end fw-bold text-dark" style={{ fontSize: "12px" }}>
                              {formatINR(item.amount)}
                            </td>
                            <td className="py-1 px-1 text-center">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm p-1 border-0"
                                disabled={sub.items.length === 1}
                                onClick={() => removeBoqItem(aIdx, sIdx, iIdx)}
                                title="Delete Item"
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Feeder Bottom Bar */}
                  <div className="d-flex justify-content-between align-items-center py-2 px-3 bg-light border-top">
                    <span className="text-muted small" style={{ fontSize: "12px" }}>
                      Feeder Total: <strong className="text-dark">{formatINR((sub.items || []).reduce((acc, it) => acc + (it.amount || 0), 0))}</strong>
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold d-flex align-items-center gap-1 text-primary"
                      style={{ fontSize: "12px" }}
                      onClick={() => addBoqItem(aIdx, sIdx)}
                    >
                      <PlusCircle size={14} /> + Add Item Row
                    </button>
                  </div>
                </div>
              ))}

              {/* Add Feeder Button */}
              <div className="d-flex justify-content-end pt-1">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="py-1 px-3 rounded fw-semibold"
                  style={{ fontSize: "12px" }}
                  onClick={() => addSubAssembly(aIdx)}
                >
                  <Plus size={14} className="me-1" /> + Add Feeder / Section
                </Button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AssemblyTreeBuilder;
